import { useRef } from "react";

import { truncate } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { Image, Loader, Menu } from "simplydo/react/ui";

import type { SuggestionItem } from "./types";

const Wrapper = styled.div<{ $isActive: boolean; $left: number; $top: number }>`
  position: fixed;
  z-index: 999;
  min-width: 300px;
  background: #fff;
  display: ${({ $isActive }) => ($isActive ? "block" : "none")};
  border-radius: 4px;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  overflow: hidden;
  box-shadow:
    0 4px 8px rgba(12, 43, 65, 0.25),
    0 0 1px rgba(12, 43, 65, 0.31);
`;

const Avatar = styled(Image)`
  object-fit: cover;
  width: 28px;
  height: 28px;

  &&& {
    margin-right: 10px;
  }
`;

const Emoji = styled.div`
  font-size: 28px;
  align-self: center;
  margin-right: 10px;
`;

const Label = styled.div`
  font-size: 15px;
  font-weight: bold;
`;

const Description = styled.div`
  color: #777;
`;

type DropdownProps = {
  suggestions: Array<SuggestionItem>;
  loading: boolean;
  isActive: boolean;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  createMention: (index: number) => void;
  clientRect: (() => DOMRect | null) | null;
};

const Dropdown = ({
  suggestions,
  loading,
  isActive,
  activeIndex,
  setActiveIndex,
  createMention,
  clientRect,
}: DropdownProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const rect = clientRect?.() ?? null;

  return (
    <Wrapper
      $top={(rect?.bottom ?? 0) + 6}
      $left={rect?.left ?? 0}
      $isActive={isActive && !!rect}
      style={{ minHeight: 60 }}
    >
      {loading ? (
        <Loader size="small" centered="inline" active={loading} />
      ) : null}
      {isActive ? (
        <Menu
          vertical
          style={{
            width: "100%",
            minHeight: 60,
            maxHeight: 300,
            overflowY: "auto",
            marginTop: 0,
          }}
          ref={menuRef}
        >
          {suggestions.length > 0 || loading ? (
            suggestions.map((item, index) => {
              const isHighlighted = index === activeIndex;
              return (
                <Menu.Item
                  key={`${item.kind}-${item.id}`}
                  style={isHighlighted ? { background: "#f0f4fa" } : {}}
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    createMention(index);
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {item.kind === "mention" && item.image ? (
                      <Avatar avatar src={item.image} />
                    ) : null}
                    {item.kind === "emoji" ? <Emoji>{item.emoji}</Emoji> : null}
                    <div>
                      <Label>{item.label || ""}</Label>
                      <Description>
                        {truncate(item.description || "", 50)}
                      </Description>
                    </div>
                  </div>
                </Menu.Item>
              );
            })
          ) : (
            <Menu.Item>
              <Label>Sorry, no results found</Label>
              <Description>Start typing or adjust your query.</Description>
            </Menu.Item>
          )}
        </Menu>
      ) : null}
    </Wrapper>
  );
};

export default Dropdown;
